/**
 * `Field` — gruppo etichetta + controllo con `htmlFor`/`id` collegati e un hint
 * opzionale. `TextInput` è un `<input>` che eredita gli stili base globali.
 *
 * Manteniamo l'associazione label↔input esplicita (accessibilità): `Field`
 * genera l'`id` e lo passa via render-prop, oppure lo si fornisce dall'esterno.
 */
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  /** Nasconde visivamente l'etichetta mantenendola per gli screen reader. */
  hideLabel?: boolean;
  children: ReactNode;
}

export function Field({ id, label, hint, hideLabel = false, children }: FieldProps) {
  return (
    <div className="ui-field">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'ui-field__label'}>
        {label}
      </label>
      {children}
      {hint && (
        <span className="ui-field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement>;

export function TextInput({ className, type = 'text', ...rest }: TextInputProps) {
  return <input type={type} className={className} {...rest} />;
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * Area di testo multiriga che eredita gli stili base globali degli input.
 * Default a 3 righe; ridimensionabile solo in verticale (vedi `styles.css`).
 */
export function TextArea({ className, rows = 3, ...rest }: TextAreaProps) {
  return <textarea rows={rows} className={className} {...rest} />;
}
